<template>
  <!-- 操作栏 -->
  <el-row justify="end">
    <el-button @click="openForm">
      <Icon class="mr-5px" icon="clarity:contract-line" />
      创建合同
    </el-button>
  </el-row>

  <!-- 列表 -->
  <ContentWrap class="mt-10px">
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <!-- ★ 列 / 列宽 / 插槽与「合同管理」(views/crm/contract/index.vue) 保持一致 (2026-09-20)
           有意差异：
           ① 不显示「客户名称」列 —— 本组件只用于客户详情 / 商机详情的「合同」页签，
              列表已按单个客户过滤，该列恒为同一个值。
           ② 不带「操作」列（编辑 / 提交审核 / 查看审批 / 详情 / 删除）——
              这里是查看 + 新建入口，操作请去合同管理。
           ③ 合同状态不做 fixed="right" —— 详情页容器比管理页窄。 -->
      <el-table-column align="center" fixed="left" label="合同编号" prop="no" width="180" />
      <!-- 2026-09-21 按原型补充 -->
      <el-table-column align="center" label="订单编号" prop="orderNo" width="120" />
      <el-table-column align="center" fixed="left" label="合同名称" prop="name" width="160">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="商机名称" prop="businessName" width="130">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openBusinessDetail(scope.row.businessId)"
          >
            {{ scope.row.businessName }}
          </el-link>
        </template>
      </el-table-column>
      <!-- 2026-09-21 按原型补充：合同类型 / 签约日期 -->
      <el-table-column align="center" label="合同类型" prop="contractType" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_CONTRACT_TYPE" :value="scope.row.contractType" />
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="签约日期"
        prop="signDate"
        width="120"
        :formatter="dateFormatter2"
      />
      <el-table-column
        align="center"
        label="合同金额（元）"
        prop="totalPrice"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <!-- 2026-09-21 按原型补充：付款方式 / 收付款 -->
      <el-table-column align="center" label="付款方式" prop="paymentMethod" width="100">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_METHOD"
            :value="scope.row.paymentMethod"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="收/付款" prop="paymentDirection" width="100">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_DIRECTION"
            :value="scope.row.paymentDirection"
          />
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        label="下单时间"
        prop="orderDate"
        width="120"
        :formatter="dateFormatter2"
      />
      <el-table-column
        align="center"
        label="合同开始时间"
        prop="startTime"
        width="120"
        :formatter="dateFormatter2"
      />
      <!-- 原型叫「到期日期」；字段仍是 end_time，只是改了 label（2026-09-21） -->
      <el-table-column
        align="center"
        label="到期日期"
        prop="endTime"
        width="120"
        :formatter="dateFormatter2"
      />
      <el-table-column align="center" label="客户签约人" prop="contactName" width="130">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openContactDetail(scope.row.signContactId)"
          >
            {{ scope.row.signContactName }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="公司签约人" prop="signUserName" width="130" />
      <el-table-column align="center" label="备注" prop="remark" width="200" />
      <!-- 2026-09-21 按原型补充：说明（与「备注」remark 语义不同，两者并存） -->
      <el-table-column align="center" label="说明" prop="description" width="200" />
      <el-table-column
        align="center"
        label="已回款金额（元）"
        prop="totalReceivablePrice"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <!-- ⚠️ 这列必须带 #default 插槽：CrmContractRespVO 里没有「未回款」字段，
           靠插槽算 totalPrice - totalReceivablePrice（与合同管理列表一致）。
           不要只抄 prop，抄了会显示成「已回款」的同一个数。
           ★ 2026-09-21 按原型把 label 改成「待收/付金额」，并按收付款方向加前缀。 -->
      <el-table-column align="center" label="待收/付金额（元）" width="150">
        <template #default="scope">
          {{ pendingAmountLabel(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="最后跟进时间"
        prop="contactLastTime"
        width="180px"
      />
      <el-table-column align="center" label="负责人" prop="ownerUserName" width="120" />
      <el-table-column align="center" label="所属部门" prop="ownerUserDeptName" width="100px" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="更新时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180px"
      />
      <el-table-column align="center" label="创建人" prop="creatorName" width="120" />
      <!-- 2026-09-21 按原型补充：申请人部门。
           ⚠️ 是**申请人（creator）**的部门，与上面的「所属部门」= 负责人（ownerUserId）的部门口径不同。 -->
      <el-table-column align="center" label="申请人部门" prop="creatorDeptName" width="110px" />
      <!-- ★ 「合同状态」列（即审批状态 auditStatus）按需求隐藏（2026-09-21）：审批入口已下线，
           该列恒为「未提交」。恢复：取消注释即可。 -->
      <!-- <el-table-column align="center" label="合同状态" prop="auditStatus" width="120">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_AUDIT_STATUS" :value="scope.row.auditStatus" />
        </template>
      </el-table-column> -->
    </el-table>
    <!-- 分页 -->
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加 -->
  <ContractForm ref="formRef" @success="getList" />
</template>
<script setup lang="ts">
import * as ContractApi from '@/api/crm/contract'
import ContractForm from './../ContractForm.vue'
import { BizTypeEnum } from '@/api/crm/permission'
import { dateFormatter, dateFormatter2 } from '@/utils/formatTime'
import { DICT_TYPE } from '@/utils/dict'
import { erpPriceInputFormatter, erpPriceTableColumnFormatter } from '@/utils'

defineOptions({ name: 'CrmContractList' })
const props = defineProps<{
  bizType: number // 业务类型
  bizId: number // 业务编号
}>()

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  customerId: undefined as number | undefined,
  businessId: undefined as number | undefined
})

/** 「待收/付金额」列：按收付款方向加前缀（与合同管理列表口径一致） */
const pendingAmountLabel = (row: ContractApi.ContractVO) => {
  // 付款单没有付款记录来源，「待付」只能取合同金额，语义是「应付总额」
  if (row.paymentDirection === 2) {
    return '待付' + erpPriceInputFormatter(row.totalPrice ?? 0)
  }
  return '待收' + erpPriceInputFormatter((row.totalPrice ?? 0) - (row.totalReceivablePrice ?? 0))
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    // 置空参数
    queryParams.customerId = undefined
    queryParams.businessId = undefined
    // 执行查询
    let data = { list: [], total: 0 }
    switch (props.bizType) {
      case BizTypeEnum.CRM_CUSTOMER:
        queryParams.customerId = props.bizId
        data = await ContractApi.getContractPageByCustomer(queryParams)
        break
      case BizTypeEnum.CRM_BUSINESS:
        queryParams.businessId = props.bizId
        data = await ContractApi.getContractPageByBusiness(queryParams)
        break
      default:
        return
    }
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 添加 */
const formRef = ref()
const openForm = () => {
  formRef.value.open('create')
}

/** 打开合同详情 */
const { push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmContractDetail', params: { id } })
}

/** 打开商机详情（对齐合同管理列表：商机名称可点击跳转） */
const openBusinessDetail = (id: number) => {
  push({ name: 'CrmBusinessDetail', params: { id } })
}

/** 打开联系人详情（对齐合同管理列表：客户签约人可点击跳转） */
const openContactDetail = (id: number) => {
  push({ name: 'CrmContactDetail', params: { id } })
}

/** 监听打开的 bizId + bizType，从而加载最新的列表 */
watch(
  () => [props.bizId, props.bizType],
  () => {
    handleQuery()
  },
  { immediate: true, deep: true }
)
</script>
